import type { TFunction } from 'i18next';
import { Timer, Warehouse, Zap } from 'lucide-react';

import { Money } from '@/shared/ui';
import { formatDate } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';

import type { StaticOrderDetail } from '../lib/orderDetailStatic';

export interface OrderDetailMainColumnProps {
  detail: StaticOrderDetail;
  t: TFunction<'orders'>;
}

export function OrderDetailMainColumn({ detail, t }: OrderDetailMainColumnProps) {
  const targetTime = formatDate(detail.targetDeliveryAtIso, 'time');
  const hubCode = detail.hubCode?.trim();
  const discountPaise = detail.discountPaise ?? 0;
  const handlingFeePaise = detail.handlingFeePaise ?? 0;

  return (
    <div className="flex min-w-0 flex-col gap-8 lg:col-span-8">
      <div className="grid gap-4 sm:grid-cols-3">
        <article className="flex gap-4 rounded-2xl border border-border bg-surface p-5 shadow-sm">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-subtle text-primary"
            aria-hidden
          >
            <Warehouse className="h-5 w-5" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wide text-fg-muted">
              {t('detail.quick.hubLabel')}
            </p>
            <p className="mt-0.5 text-sm font-semibold text-fg">
              {detail.hubName}
              {hubCode ? (
                <span className="font-medium text-fg-muted"> ({hubCode})</span>
              ) : null}
            </p>
          </div>
        </article>

        <article className="flex gap-4 rounded-2xl border border-border bg-surface p-5 shadow-sm">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-info-subtle text-info-strong"
            aria-hidden
          >
            <Timer className="h-5 w-5" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wide text-fg-muted">
              {t('detail.quick.deliveryLabel')}
            </p>
            <p className="mt-0.5 text-sm font-semibold text-fg">
              {t('detail.quick.targetLine', { time: targetTime })}
            </p>
            {detail.promisedDeliveryMinutes > 0 ? (
              <p className="mt-0.5 text-xs text-fg-muted">
                {t('detail.quick.promisedMinutes', {
                  minutes: detail.promisedDeliveryMinutes,
                })}
              </p>
            ) : null}
          </div>
        </article>

        <article className="flex gap-4 rounded-2xl border border-border bg-surface p-5 shadow-sm">
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
              detail.slaBreached
                ? 'bg-danger-subtle text-danger-strong'
                : 'bg-warning-subtle text-warning-strong',
            )}
            aria-hidden
          >
            <Zap className="h-5 w-5" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wide text-fg-muted">
              {t('detail.quick.priorityLabel')}
            </p>
            <p className="mt-0.5 text-sm font-semibold text-fg">
              {detail.priorityLabelKey === 'detail.quick.prioritySla' &&
              detail.promisedDeliveryMinutes > 0
                ? t('detail.quick.prioritySla', { minutes: detail.promisedDeliveryMinutes })
                : t(detail.priorityLabelKey as 'detail.quick.priorityExpress')}
            </p>
            {detail.slaBreached ? (
              <p className="mt-0.5 text-xs font-medium text-danger-strong">
                {t('detail.quick.slaBreached')}
              </p>
            ) : null}
          </div>
        </article>
      </div>

      <section
        className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm"
        aria-labelledby="order-items-heading"
      >
        <div className="border-b border-border px-6 py-5">
          <h2
            id="order-items-heading"
            className="flex flex-wrap items-baseline gap-2 text-lg font-bold text-fg"
          >
            <span>{t('detail.items.heading')}</span>
            <span className="text-sm font-normal text-fg-muted">
              {t('detail.items.itemsTotal', { count: detail.totalItemCount })}
            </span>
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse text-left text-sm">
            <caption className="sr-only">{t('detail.items.caption')}</caption>
            <thead>
              <tr className="border-b border-border bg-surface-muted">
                <th
                  scope="col"
                  className="px-6 py-4 text-[11px] font-bold uppercase tracking-wide text-fg-muted"
                >
                  {t('detail.items.cols.product')}
                </th>
                <th
                  scope="col"
                  className="px-6 py-4 text-center text-[11px] font-bold uppercase tracking-wide text-fg-muted"
                >
                  {t('detail.items.cols.qty')}
                </th>
                <th
                  scope="col"
                  className="px-6 py-4 text-right text-[11px] font-bold uppercase tracking-wide text-fg-muted"
                >
                  {t('detail.items.cols.price')}
                </th>
                <th
                  scope="col"
                  className="px-6 py-4 text-right text-[11px] font-bold uppercase tracking-wide text-fg-muted"
                >
                  {t('detail.items.cols.total')}
                </th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((line) => (
                <tr key={line.id} className="border-b border-border last:border-b-0">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-surface-muted text-xs font-semibold text-fg-muted">
                        {line.imageSrc ? (
                          <img
                            src={line.imageSrc}
                            alt={line.imageAlt}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span aria-hidden>{line.name.slice(0, 1).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-fg">{line.name}</div>
                        {line.pickedQuantity != null && line.pickedQuantity !== line.quantity ? (
                          <div className="mt-0.5 text-xs font-medium text-fg-muted">
                            {t('detail.items.picked', {
                              picked: line.pickedQuantity,
                              total: line.quantity,
                            })}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center font-medium tabular-nums text-fg">
                    {line.quantity}
                  </td>
                  <td className="px-6 py-4 text-right font-normal text-fg-muted">
                    <Money amount={line.unitPricePaise} />
                  </td>
                  <td className="px-6 py-4 text-right font-bold tabular-nums text-fg">
                    <Money amount={line.lineTotalPaise} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border-t border-border bg-surface-muted/50 px-6 py-6 sm:px-8">
          <div className="ml-auto flex w-full max-w-xs flex-col gap-3">
            <div className="flex items-start justify-between gap-4 text-sm">
              <span className="text-fg-muted">{t('detail.summary.subtotal')}</span>
              <span className="font-medium tabular-nums text-fg">
                <Money amount={detail.subtotalPaise} />
              </span>
            </div>
            <div className="flex items-start justify-between gap-4 text-sm">
              <span className="text-fg-muted">{t('detail.summary.deliveryFee')}</span>
              <span className="font-medium tabular-nums text-fg">
                {detail.deliveryFeePaise > 0 ? (
                  <Money amount={detail.deliveryFeePaise} />
                ) : (
                  t('detail.summary.free')
                )}
              </span>
            </div>
            {handlingFeePaise > 0 ? (
              <div className="flex items-start justify-between gap-4 text-sm">
                <span className="text-fg-muted">{t('detail.summary.handlingFee')}</span>
                <span className="font-medium tabular-nums text-fg">
                  <Money amount={handlingFeePaise} />
                </span>
              </div>
            ) : null}
            {detail.taxPaise > 0 ? (
              <div className="flex items-start justify-between gap-4 text-sm">
                <span className="text-fg-muted">{t('detail.summary.taxGst')}</span>
                <span className="font-medium tabular-nums text-fg">
                  <Money amount={detail.taxPaise} />
                </span>
              </div>
            ) : null}
            {discountPaise > 0 ? (
              <div className="flex items-start justify-between gap-4 border-b border-border pb-3 text-sm">
                <span className="text-fg-muted">{t('detail.summary.discount')}</span>
                <span className="font-medium tabular-nums text-success-strong">
                  −
                  <Money amount={discountPaise} />
                </span>
              </div>
            ) : (
              <div className="border-b border-border pb-3" />
            )}
            <div className="flex items-center justify-between pt-1">
              <span className="text-base font-bold text-fg">{t('detail.summary.orderTotal')}</span>
              <span className="text-2xl font-bold tracking-tight text-primary">
                <Money amount={detail.totalPaise} />
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
